/**
 * The dash policy — the house rule: no em dashes on screen.
 *
 * An em dash is a typographic mark, not a piece of information, and the product's
 * house style bans it. The rule has to hold on every surface: menu copy, receipts,
 * the POS screen, the courier app.
 *
 * `menuraq-pos-mobile` and `menuraq-chow-mobile` apply this at the render boundary
 * (`src/ui/dashes.ts` there) because React Native has no DOM. The web cannot do
 * that for the same reason it could not do it before: the text is scattered across
 * ~200 UI files and across strings that arrive from the database (a business name,
 * a menu item description), so the policy has always been enforced at the DOM.
 *
 * WHAT CHANGED HERE — and why this file exists at all
 * ────────────────────────────────────────────────────
 * The web enforced the policy with `public/dash-cleaner.js`, loaded by
 * `app/layout.tsx` with `strategy="afterInteractive"`. That script walked the
 * document and rewrote text nodes. It ran while React was still hydrating, so
 * React compared the server's text against a DOM it had already had mutated and
 * threw:
 *
 *   Hydration failed because the server rendered text didn't match the client.
 *
 * on every page whose copy contains a dash — the guest menu included. React
 * discards the server HTML for that subtree and re-renders it on the client, so
 * the page paid twice: once on the server, once on the browser.
 *
 * Two things were wrong with the old script, and both are fixed here:
 *
 *  1. IT RAN TOO EARLY. The scrub is now applied to text React has already
 *     claimed (`components/layout/DashCleaner.tsx`), never to text React is about
 *     to hydrate. Hydration compares text during the render phase; a DOM that
 *     was rewritten before that comparison is a mismatch, always.
 *
 *  2. IT DELETED THE EN DASH. Deleting `–` turns `formatPrepRange()` —
 *     "10–15 min" — into "1015 min", a range that reads as one large number.
 *     The native apps map an en dash to a HYPHEN, which keeps the range
 *     readable and matches the `PREP_RANGES` chips ("5-10"). The web now does
 *     the same, so the two surfaces agree.
 *
 * The em dash keeps its own behaviour: it is removed, and it takes the space it
 * sat between, so "Order approved — the guest can now pay" reads as one clean
 * sentence instead of "Order approved  the guest can now pay" with a hole in it.
 */

/** Matches the two dashes the policy touches. Fast path: most strings have neither. */
const DASH = /[\u2013\u2014]/;

/** An em dash plus the spaces around it, so the removal does not leave a double space behind. */
const EM_DASH_WITH_SPACES = /[ \t]*\u2014[ \t]*/g;

/**
 * Does this string carry a dash the policy touches?
 *
 * Kept separate from `stripDashes` because the DOM scrub calls it on every text
 * node on the page; the common answer is "no", and that answer must be cheap.
 */
export function hasDash(value: string): boolean {
  return DASH.test(value);
}

/**
 * Apply the dash policy to a string.
 *
 *   'Order approved — the guest can now pay.' → 'Order approved the guest can now pay.'
 *   '10–15 min'                              → '10-15 min'
 *   'café—bar'                               → 'cafébar'
 *
 * An em dash is removed and takes the space it sat between with it. HTML would
 * collapse the two spaces it leaves behind, but the same function also runs
 * against text that never goes through the HTML parser, so the spacing is
 * settled here, once, for every surface.
 */
export function stripDashes(value: string): string {
  if (!DASH.test(value)) return value;
  return value
    .replace(EM_DASH_WITH_SPACES, (match) => (/[ \t]/.test(match) ? ' ' : ''))
    .replace(/\u2013/g, '-');
}
